import * as mapboxgl from 'mapbox-gl';
import { Map, LngLat, LngLatBounds, Popup } from 'mapbox-gl';
import * as defaultStyles from '@mapbox/mapbox-gl-draw/src/lib/theme';
import * as turfCircle from '@turf/circle';
import { Feature } from 'geojson';
import { randomColor } from 'randomcolor';

import { ITriggerZone } from './sharedModels';
import { TriggerZoneModel } from './_generated/TriggerZoneModel';
import { TriggerZoneType } from './_generated/TriggerZoneType';

const MAPBOX_API_KEY = 'pk.eyJ1Ijoic21hcnQtZ3VpZGUiLCJhIjoiY2s0c2dyaWR4MDA0eDNrbW00czFwczc4cyJ9.2FnIFSxmPsJkD-o5NLOUPA';
export const DEFAULT_CIRCLE_TRIGGER_ZONE_RADIUS_M = 25;

// @ts-ignore
mapboxgl.accessToken = MAPBOX_API_KEY;

export function createMap(container: HTMLElement, bounds?: mapboxgl.LngLatBoundsLike) {
    const map = new Map({
        container,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [0, 0],
        zoom: 9,
        logoPosition: 'bottom-right',
        preserveDrawingBuffer: true,
        bounds: bounds
    });
    map.addControl(new mapboxgl.ScaleControl());

    // wheel zoom only with Ctrl/Command pressed
    map.scrollZoom.disable();
    map.on("wheel", event => {
        if (event.originalEvent.ctrlKey) {
            event.originalEvent.preventDefault(); // Prevent chrome/firefox default behavior
            if (!map.scrollZoom.isEnabled()) map.scrollZoom.enable();
        } else {
            if (map.scrollZoom.isEnabled()) map.scrollZoom.disable();
        }
    });

    map.addControl(new mapboxgl.FullscreenControl());

    return map;
}

export function lngLatArrayToBounds(lngLats: LngLat[]) {
    const lats = lngLats.map(ll => ll.lat);
    const lngs = lngLats.map(ll => ll.lng);

    const southWest = new LngLat(Math.min(...lngs), Math.min(...lats));
    const northEast = new LngLat(Math.max(...lngs), Math.max(...lats));

    return new LngLatBounds(southWest, northEast);
}

// styling docs: https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/EXAMPLES.md
export function getDrawStyles() {
    const customStyles = [
        {
            "id": "gl-draw-line-static-custom-color",
            "type": "line",
            "filter": ["all",
                ["==", "$type", "LineString"],
                ["==", "active", "false"],
                ["has", "user_color"],
            ],
            "layout": {
                "line-cap": "round",
                "line-join": "round"
            },
            "paint": {
                "line-color": ["get", "user_color"],
                "line-width": 3
            }
        },

        {
            "id": "gl-draw-polygon-stroke-custom-color",
            "type": "line",
            "filter": ["all",
                ["==", "$type", "Polygon"],
                ["==", "active", "false"],
                ["has", "user_color"],
            ],
            "paint": {
                "line-color": ["get", "user_color"],
                'line-width': 1,
                'line-opacity': 0.6
            }
        },
        {
            "id": "gl-draw-polygon-fill-custom-color",
            "type": "fill",
            "filter": ["all",
                ["==", "$type", "Polygon"],
                ["==", "active", "false"],
                ["has", "user_color"],
            ],
            "paint": {
                "fill-color": ["get", "user_color"],
                "fill-opacity": 0.2
            }
        },


        {
            "id": "gl-draw-polygon-fill",
            "type": "fill",
            "filter": ["all",
                ["==", "$type", "Polygon"],
                ["==", "active", "true"],
                ["==", "mode", "direct_select"],
            ],
            "paint": {
                "fill-color": "#D20C0C",
                "fill-outline-color": "#D20C0C",
                "fill-opacity": 0.2
            }
        },
        {
            "id": "gl-draw-polygon-and-line-vertex-halo-active",
            "type": "circle",
            "filter": ["all",
                ["==", "meta", "vertex"],
                ["==", "$type", "Point"],
                ["==", "active", "true"],
            ],
            "paint": {
                "circle-radius": 4,
                "circle-color": "red"
            }
        },

        {
            'id': 'gl-draw-polygon-and-line-vertex-stroke-inactive',
            'type': 'circle',
            'filter': ['all',
                ['==', 'meta', 'vertex'],
                ['==', '$type', 'Point'],
                ['!=', 'mode', 'static']
            ],
            'paint': {
                'circle-radius': 5,
                'circle-color': '#000'
            }
        },

        {
            'id': 'gl-draw-polygon-and-line-vertex-inactive',
            'type': 'circle',
            'filter': ['all',
                ['==', 'meta', 'vertex'],
                ['==', '$type', 'Point'],
                ['!=', 'mode', 'static']
            ],
            'paint': {
                'circle-radius': 4,
                'circle-color': 'lime'
            }
        },

        {
            'id': 'gl-draw-point-active',
            'type': 'circle',
            'filter': ['all',
                ['==', '$type', 'Point'],
                ['!=', 'meta', 'midpoint'],
                ['==', 'active', 'true']],
            'paint': {
                'circle-radius': 5,
                'circle-color': 'red'
            }
        },

        {
            'id': 'gl-draw-polygon-midpoint',
            'type': 'circle',
            'filter': ['all',
                ['==', '$type', 'Point'],
                ['==', 'meta', 'midpoint']],
            'paint': {
                'circle-radius': 3,
                'circle-color': '#9A9A9A'
            }
        }, 

    ];

    const baseStyles = [...(defaultStyles.default)];

    return [
        ...(baseStyles.filter(s => !customStyles.find(x => x.id === s.id))),
        ...customStyles
    ];
}

export function buildMapFeatureForTriggerZone(
    z: ITriggerZone | TriggerZoneModel,
    setRandomColor = true,
    description: string | undefined = undefined
): Feature {
    let feature: Feature | undefined = undefined;
    if (z.type === TriggerZoneType.Polygon) {
        const coordinates = [(z.polygonTypePolygonCorners || []).map(c => ([c.longitude, c.latitude]))];

        // close the polygon if it's not closed as Mapbox/turf supports just closed polygons
        if (coordinates && coordinates[0] && coordinates[0].length > 1) {
            const firstPoint = coordinates[0][0];
            const lastPoint = coordinates[0][coordinates[0].length - 1];
            if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                coordinates[0].push(firstPoint);
            }
        }

        feature = {
            type: 'Feature',
            properties: {
                description: description
            },
            geometry:
            {
                type: 'Polygon',
                coordinates
            }
        };
    }
    else if (z.type === TriggerZoneType.Circle) {
        const center = [z.circleTypeGpsCoordinates?.longitude || 0, z.circleTypeGpsCoordinates?.latitude || 0];
        feature = turfCircle.default(center, z.circleTypeRadius || DEFAULT_CIRCLE_TRIGGER_ZONE_RADIUS_M, { steps: 64, units: 'meters' });
        feature.properties = {
            isCircle: true,
            center,
            radiusInKm: (z.circleTypeRadius || DEFAULT_CIRCLE_TRIGGER_ZONE_RADIUS_M) / 1000,
            description: description
        };
    }
    if (!feature) {
        throw new Error(`Unsupported trigger zone type: ${z.type}`);
    }

    if (setRandomColor) {
        feature.properties = {
            ...feature.properties,
            color: randomColor()
        };
    }
    return feature;
}

function loadImage(map: mapboxgl.Map, url: string) {
    return new Promise<HTMLImageElement | ImageBitmap | undefined>((resolve, reject) => {
        map.loadImage(url, (error, result) => {
            if (error) {
                reject(error);
                return;
            }

            resolve(result);
        });
    });
}

export async function loadIcon(map: mapboxgl.Map, url: string, name: string, options?: { pixelRatio?: number; sdf?: boolean }) {
    const placeIcon = await loadImage(map, url);
    if (placeIcon) {
        map.addImage(name, placeIcon, options);
    }
}

const ROUTE_COLORS = ['#9105BF', '#FFCD00', '#0076FF', '#44DB5E', '#FF3824', '#000000', '#FF9600', '#8E44AD', '#54C7FC', '#8E8E93'];

export function getTourColor(index: number) {
    return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

// trigger zone popup idea taken from https://bl.ocks.org/malwoodsantoro/6696fedafc3e150315dd1d97adc65003
export function showTriggerZonePopup(map: Map) {
    const triggerZonePopup = new Popup();

    map.on("click", "gl-draw-polygon-fill-custom-color.cold", e => {

        if (!e.features
            || e.features.length < 1
            || !e.features[0].properties
            || !e.features[0].properties.user_description) return;

        triggerZonePopup
            .setLngLat(e.lngLat)
            .setHTML(e.features[0].properties.user_description)
            .addTo(map);
    });
}

// taken from the app source code
export function getOsmId(id: string | number) {
  const fixedId = `${id}`;
  // OSM IDs look like 'node:12345', 'way:789456', 'relation:666333'
  const types: { [key: string]: string } = {
    '0': 'node',
    '1': 'way',
    '4': 'relation',
  };
  const type = fixedId[fixedId.length - 1];
  const sourceId = fixedId.substr(0, fixedId.length - 1);

  if (!types[type] || !sourceId) return;

  return `${types[type]}:${sourceId}`;
}